import { lazy, Suspense, useState, useEffect } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { LanguageProvider } from "@/i18n/LanguageContext";
import { ThemeProvider } from "@/hooks/use-theme";
import { supabase } from "@/integrations/supabase/client";
import MaintenancePage from "@/components/MaintenancePage";
import Index from "./pages/Index";

// ⬇️ Cambia a true per attivare la manutenzione
const MAINTENANCE_MODE = false;

// Lazy-loaded pages
const NotFound = lazy(() => import("./pages/NotFound"));
const ChiSiamo = lazy(() => import("./pages/ChiSiamo"));
const Storia = lazy(() => import("./pages/Storia"));
const Organizzazione = lazy(() => import("./pages/Organizzazione"));
const NoiEAnpas = lazy(() => import("./pages/NoiEAnpas"));
const Trasparenza = lazy(() => import("./pages/Trasparenza"));
const NostriMezzi = lazy(() => import("./pages/NostriMezzi"));
const Servizi = lazy(() => import("./pages/Servizi"));
const TrasportoSanitario = lazy(() => import("./pages/TrasportoSanitario"));
const Teleassistenza = lazy(() => import("./pages/Teleassistenza"));
const AssistenzaEventi = lazy(() => import("./pages/AssistenzaEventi"));
const CorsiFormazione = lazy(() => import("./pages/CorsiFormazione"));
const GuardiaMedica = lazy(() => import("./pages/GuardiaMedica"));
const ServizioCivile = lazy(() => import("./pages/ServizioCivile"));
const Emergenza112 = lazy(() => import("./pages/Emergenza112"));
const DiventaVolontario = lazy(() => import("./pages/DiventaVolontario"));
const Iniziative = lazy(() => import("./pages/Iniziative"));

const ProgettiAssociazioni = lazy(() => import("./pages/ProgettiAssociazioni"));
const StellaAmbulanza = lazy(() => import("./pages/StellaAmbulanza"));
const News = lazy(() => import("./pages/News"));
const Eventi = lazy(() => import("./pages/Eventi"));
const Contatti = lazy(() => import("./pages/Contatti"));
const LeSedi = lazy(() => import("./pages/LeSedi"));
const LavoraConNoi = lazy(() => import("./pages/LavoraConNoi"));
const Sostienici = lazy(() => import("./pages/Sostienici"));
const DonazioniFivex = lazy(() => import("./pages/DonazioniFivex"));
const DiventaAmico = lazy(() => import("./pages/DiventaAmico"));
const LascitoTestamentario = lazy(() => import("./pages/LascitoTestamentario"));
const SOSCittadino = lazy(() => import("./pages/SOSCittadino"));
const GestioneSOS = lazy(() => import("./pages/GestioneSOS"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const CookiePolicy = lazy(() => import("./pages/CookiePolicy"));

const queryClient = new QueryClient();

const App = () => {
  const [authenticated, setAuthenticated] = useState(false);
  const [checkingAuth, setCheckingAuth] = useState(MAINTENANCE_MODE);

  useEffect(() => {
    if (!MAINTENANCE_MODE) return;

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setAuthenticated(!!session);
      setCheckingAuth(false);
    });

    supabase.auth.getSession().then(({ data: { session } }) => {
      setAuthenticated(!!session);
      setCheckingAuth(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  if (MAINTENANCE_MODE && checkingAuth) {
    return null; // brief loading
  }

  if (MAINTENANCE_MODE && !authenticated) {
    return <MaintenancePage onLoginSuccess={() => setAuthenticated(true)} />;
  }

  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
      <LanguageProvider>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <Suspense fallback={null}>
            <Routes>
              <Route path="/" element={<Index />} />
              <Route path="/chi-siamo" element={<ChiSiamo />} />
              <Route path="/chi-siamo/storia" element={<Storia />} />
              <Route path="/chi-siamo/organizzazione" element={<Organizzazione />} />
              <Route path="/chi-siamo/organizzazione/organi-associativi" element={<Organizzazione />} />
              <Route path="/chi-siamo/organizzazione/volontari" element={<Organizzazione />} />
              <Route path="/chi-siamo/organizzazione/dipendenti" element={<Organizzazione />} />
              <Route path="/chi-siamo/noi-e-anpas" element={<NoiEAnpas />} />
              <Route path="/chi-siamo/trasparenza" element={<Trasparenza />} />
              <Route path="/chi-siamo/i-nostri-mezzi" element={<NostriMezzi />} />
              <Route path="/servizi" element={<Servizi />} />
              <Route path="/servizi/trasporto-sanitario-programmato" element={<TrasportoSanitario />} />
              <Route path="/servizi/teleassistenza" element={<Teleassistenza />} />
              <Route path="/servizi/assistenza-manifestazioni-eventi" element={<AssistenzaEventi />} />
              <Route path="/servizi/corsi-formazione-sanitaria" element={<CorsiFormazione />} />
              <Route path="/servizi/assistenza-guardia-medica" element={<GuardiaMedica />} />
              <Route path="/servizi/servizio-civile" element={<ServizioCivile />} />
              <Route path="/servizi/emergenza-sanitaria-112" element={<Emergenza112 />} />
              <Route path="/diventa-volontario" element={<DiventaVolontario />} />
              <Route path="/iniziative" element={<Iniziative />} />
              
              <Route path="/iniziative/una-stella-in-ambulanza" element={<StellaAmbulanza />} />
              <Route path="/iniziative/progetti-con-altre-associazioni" element={<ProgettiAssociazioni />} />
              <Route path="/news" element={<News />} />
              <Route path="/eventi" element={<Eventi />} />
              <Route path="/contatti" element={<Contatti />} />
              <Route path="/contatti/le-sedi" element={<LeSedi />} />
              <Route path="/contatti/lavora-con-noi" element={<LavoraConNoi />} />
              <Route path="/sostienici" element={<Sostienici />} />
              <Route path="/sostienici/5x1000-donazioni" element={<DonazioniFivex />} />
              <Route path="/sostienici/diventa-amico-avps" element={<DiventaAmico />} />
              <Route path="/sostienici/lascito-testamentario" element={<LascitoTestamentario />} />
              <Route path="/sos-cittadino" element={<SOSCittadino />} />
              <Route path="/gestione-sos" element={<GestioneSOS />} />
              <Route path="/privacy" element={<PrivacyPolicy />} />
              <Route path="/cookie-policy" element={<CookiePolicy />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </BrowserRouter>
      </TooltipProvider>
      </LanguageProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
};

export default App;
